import { createContext, useContext } from "react";
import type { CaseDocumentPendingSubmit } from "./CaseDocumentTabStrip";

/**
 * Derived, cross-section completion state written by MonitoringCaseSection and read by
 * IncidentForm.tsx's FormActionsBar (Submit/Complete buttons) - a plain React Context, not RHF's
 * own `context` option (investigated: that's a static value passed once to `useForm`, consumed
 * only by validation resolvers - it has no setter and isn't reactive to changes, so it can't serve
 * as a live read/write channel between sections). None of this is form data (never part of
 * buildPayload's submitted record), which is exactly what plain Context is for.
 */
export interface IncidentMonitoringGating {
  pathAComplete: boolean;
  monitorCanComplete: boolean;
  /** The active tab's pending confirm action, if any - lets the page-level Submit button work
   *  without the page itself needing to know which document slot/tab is currently selected. */
  pendingCaseDocumentSubmit: CaseDocumentPendingSubmit | undefined;
}

export const defaultGating: IncidentMonitoringGating = {
  pathAComplete: false,
  monitorCanComplete: false,
  pendingCaseDocumentSubmit: undefined,
};

export const IncidentMonitoringGatingContext = createContext<
  [IncidentMonitoringGating, (gating: IncidentMonitoringGating) => void]
>([defaultGating, () => {}]);

export const useIncidentMonitoringGating = () =>
  useContext(IncidentMonitoringGatingContext);
